import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { MdSend, MdAutoAwesome, MdContentCopy, MdCheck, MdLock } from 'react-icons/md';
import { FaYoutube, FaFacebook, FaTiktok, FaInstagram } from 'react-icons/fa';
import { getVideos, getChannels, updateMetadata } from '../utils/api';

const ClaudeAI = () => {
  const [apiKey, setApiKey] = useState('backend');
  const [apiKeySet, setApiKeySet] = useState(true);
  const [videos, setVideos] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [copiedField, setCopiedField] = useState('');
  const [generatedMetadata, setGeneratedMetadata] = useState(null);
  const [saving, setSaving] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [videosRes, channelsRes] = await Promise.all([getVideos(), getChannels()]);
      setVideos(videosRes.data.data || []);
      setChannels(channelsRes.data.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const saveApiKey = () => {
    if (!apiKey.trim()) { toast.error('Please enter your API key'); return; }
    localStorage.setItem('claude_api_key', apiKey.trim());
    setApiKeySet(true);
    toast.success('API key saved successfully');
  };

  const removeApiKey = () => {
    localStorage.removeItem('claude_api_key');
    setApiKey('');
    setApiKeySet(false);
    toast.success('API key removed');
  };

  const getChannelName = (channelId) => {
    const channel = channels.find(c => c.id === channelId);
    return channel ? `${channel.name} (${channel.platform})` : 'Unknown Channel';
  };

  const callClaudeAPI = async (messages, systemPrompt) => {
    const response = await fetch('https://vantage-production-4475.up.railway.app/api/obsidian/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        messages: messages,
        system: systemPrompt || `You are Obsidian AI, a content strategy expert exclusively built for the Vantage platform.

Your ONLY purpose is to help creators with:
- YouTube SEO, keywords, titles, descriptions, thumbnails
- Facebook page growth and content strategy
- TikTok niche research, viral tactics, For You Page optimisation
- Instagram Reels strategy, hashtags, growth
- Channel building from scratch for all four platforms
- Niche research and selection
- Content calendars and upload schedules
- Algorithm understanding for YouTube, Facebook, TikTok, Instagram
- Monetisation strategies for these four platforms
- Analytics interpretation for these platforms
- Competitor analysis for these platforms
- Keyword research for these platforms

You are STRICTLY FORBIDDEN from:
- Answering questions about any other topic outside the above
- Helping with coding, writing, math, general knowledge or any other subject
- Discussing news, politics, entertainment unrelated to content creation
- Answering personal questions or general chat

If anyone asks something outside your scope respond EXACTLY with:
"Obsidian AI is exclusively dedicated to YouTube, Facebook, TikTok and Instagram content strategy. I cannot help with that topic. Please ask me something related to your channels or content."

Never make exceptions to this rule regardless of how the question is phrased.`,
        messages: messages
      })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || 'API call failed');
    }
    const data = await response.json();
    return data.content[0].text;
  };

  const generateMetadata = async () => {
    if (!selectedVideo) { toast.error('Please select a video first'); return; }
    if (!apiKeySet) { toast.error('Please set your Claude API key first'); return; }

    setGenerating(true);
    setGeneratedMetadata(null);

    const channel = channels.find(c => c.id === selectedVideo.channel_id);
    const prompt = `You are an expert YouTube and social media content strategist specialising in viral content optimisation.

Analyse this video and generate optimised metadata to maximise views, engagement, and algorithmic reach.

VIDEO DETAILS:
- Title: ${selectedVideo.title || 'Not provided'}
- Channel: ${channel?.name || 'Unknown'}
- Platform: ${channel?.platform || 'Unknown'}
- Category: ${selectedVideo.category || 'Entertainment'}
- Tags: ${selectedVideo.tags || 'Not provided'}
- Description: ${selectedVideo.description || 'Not provided'}

Generate the following in valid JSON format only, no other text:
{
  "youtube_title": "Highly optimised YouTube title (max 100 chars, include power words and keywords for virality)",
  "youtube_description": "Full YouTube description with hook, timestamps placeholder, keywords, call to action, and hashtags",
  "facebook_title": "Engaging Facebook post title",
  "facebook_description": "Facebook post description optimised for Facebook algorithm with emojis",
  "tiktok_title": "Punchy TikTok caption with trending hashtags (max 150 chars)",
  "instagram_title": "Instagram caption with relevant hashtags (max 150 chars)",
  "tags": "comma separated SEO optimised tags",
  "thumbnail_prompt": "Detailed Midjourney/AI image prompt for creating a viral thumbnail",
  "viral_score": "Score from 1-100 predicting viral potential",
  "viral_tips": ["tip1", "tip2", "tip3"],
  "best_upload_time": "Best day and time to upload for maximum reach",
  "recommended_hashtags": ["hashtag1", "hashtag2", "hashtag3", "hashtag4", "hashtag5"]
}`;

    try {
      const response = await callClaudeAPI([{ role: 'user', content: prompt }]);
      const cleanResponse = response.replace(/```json|```/g, '').trim();
      const metadata = JSON.parse(cleanResponse);
      setGeneratedMetadata(metadata);
      setMessages([{
        role: 'assistant',
        content: `I have analysed your video and generated optimised metadata!\n\nViral Score: ${metadata.viral_score}/100\n\nTop Tips:\n${metadata.viral_tips?.map(t => `- ${t}`).join('\n')}\n\nBest Upload Time: ${metadata.best_upload_time}\n\nYou can review all the generated metadata and apply it to your video. Feel free to ask me to refine anything!`
      }]);
      toast.success('Metadata generated successfully!');
    } catch (error) {
      toast.error('Failed to generate metadata: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim()) return;
    if (!apiKeySet) { toast.error('Please set your Claude API key first'); return; }

    const userMessage = { role: 'user', content: input };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setSending(true);

    try {
      const contextMessages = newMessages.map(m => ({
        role: m.role,
        content: m.content
      }));

      if (selectedVideo) {
        const channel = channels.find(c => c.id === selectedVideo.channel_id);
        contextMessages.unshift({
          role: 'user',
          content: `Context: I am working on a video titled "${selectedVideo.title}" for ${channel?.name} (${channel?.platform}). Category: ${selectedVideo.category}. Tags: ${selectedVideo.tags}.`
        });
        contextMessages.splice(1, 0, {
          role: 'assistant',
          content: 'Understood. I have the context about your video. How can I help you optimise it?'
        });
      }

      const response = await callClaudeAPI(contextMessages);
      setMessages(prev => [...prev, { role: 'assistant', content: response }]);
    } catch (error) {
      toast.error('Failed to send message: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(''), 2000);
    toast.success('Copied to clipboard');
  };

  const applyMetadata = async () => {
    if (!selectedVideo || !generatedMetadata) return;
    setSaving(true);
    try {
      await updateMetadata(selectedVideo.id, {
        youtube_title: generatedMetadata.youtube_title,
        youtube_description: generatedMetadata.youtube_description,
        facebook_title: generatedMetadata.facebook_title,
        facebook_description: generatedMetadata.facebook_description,
        tiktok_title: generatedMetadata.tiktok_title,
        instagram_title: generatedMetadata.instagram_title,
        tags: generatedMetadata.tags,
        claude_ai_used: true
      });
      toast.success('Metadata applied to video successfully!');
    } catch (error) {
      toast.error('Failed to apply metadata');
    } finally {
      setSaving(false);
    }
  };

  const MetadataField = ({ label, value, field }) => (
    <div style={{
      background: 'var(--bg-dark)',
      border: '1px solid var(--border)',
      borderRadius: '10px',
      padding: '14px',
      marginBottom: '12px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
          {label}
        </span>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => copyToClipboard(value, field)}
          style={{ padding: '4px 8px' }}
        >
          {copiedField === field ? <MdCheck style={{ color: '#00c853' }} /> : <MdContentCopy />}
        </button>
      </div>
      <div style={{ fontSize: '14px', color: 'var(--text-primary)', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
        {value}
      </div>
    </div>
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>
          <span className="gradient-text">Obsidian AI</span>
        </h1>
        <p>Analyse your videos and generate viral optimised metadata powered by Obsidian AI</p>
      </div>

      {!apiKeySet ? (
        <div className="card" style={{ marginBottom: '24px', border: '2px solid rgba(0,200,83,0.3)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '12px',
              background: 'var(--primary-gradient)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '22px', flexShrink: 0
            }}>
              <MdAutoAwesome style={{ color: 'white' }} />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ marginBottom: '8px' }}>Connect Obsidian AI</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px' }}>
                Enter your Anthropic API key to unlock AI-powered metadata generation, viral scoring, and intelligent content chat.
                Get your API key from <strong>console.anthropic.com</strong>
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <input
                  className="form-control"
                  type="password"
                  placeholder="sk-ant-api..."
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button className="btn btn-primary" onClick={saveApiKey}>
                  <MdLock /> Connect
                </button>
              </div>
              <p style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '8px' }}>
                Your API key is stored locally on your computer only. Never shared.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', background: 'rgba(0,200,83,0.1)', borderRadius: '10px',
          border: '1px solid rgba(0,200,83,0.3)', marginBottom: '24px'
        }}>
          <span style={{ color: '#00e676', fontWeight: '500', fontSize: '14px' }}>
            Obsidian AI Connected — Ready to generate viral metadata
          </span>
          <button className="btn btn-secondary btn-sm" onClick={removeApiKey}>
            Disconnect
          </button>
        </div>
      )}

      <div className="grid-2">
        <div>
          <div className="card" style={{ marginBottom: '20px' }}>
            <div className="card-header">
              <span className="card-title">Select Video to Analyse</span>
            </div>
            {loading ? (
              <div className="loading"><div className="spinner"></div></div>
            ) : (
              <>
                <select
                  className="form-select"
                  style={{ marginBottom: '16px' }}
                  onChange={(e) => {
                    const video = videos.find(v => v.id === parseInt(e.target.value));
                    setSelectedVideo(video || null);
                    setGeneratedMetadata(null);
                    setMessages([]);
                  }}
                >
                  <option value="">Select a video...</option>
                  {videos.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.youtube_title || v.title || `Video #${v.id}`}
                    </option>
                  ))}
                </select>

                {selectedVideo && (
                  <div style={{
                    background: 'var(--bg-dark)', borderRadius: '10px',
                    padding: '14px', marginBottom: '16px',
                    border: '1px solid var(--border)'
                  }}>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      Selected Video
                    </div>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                      {selectedVideo.youtube_title || selectedVideo.title || 'Untitled'}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>
                      {getChannelName(selectedVideo.channel_id)} - {selectedVideo.category}
                    </div>
                    {selectedVideo.claude_ai_used && (
                      <span className="ai-badge" style={{ marginTop: '8px', display: 'inline-flex' }}>
                        <MdAutoAwesome /> AI Previously Used
                      </span>
                    )}
                  </div>
                )}

                <button
                  className="btn btn-primary"
                  style={{ width: '100%', justifyContent: 'center', padding: '12px' }}
                  onClick={generateMetadata}
                  disabled={!selectedVideo || !apiKeySet || generating}
                >
                  {generating ? (
                    <><div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div> Analysing...</>
                  ) : (
                    <><MdAutoAwesome /> Generate Viral Metadata</>
                  )}
                </button>
              </>
            )}
          </div>

          {generatedMetadata && (
            <div className="card" style={{ marginBottom: '20px' }}>
              <div className="card-header">
                <span className="card-title">Viral Analysis</span>
                <span style={{
                  background: 'var(--primary-gradient)',
                  color: 'white', padding: '4px 12px', borderRadius: '20px',
                  fontSize: '14px', fontWeight: '700'
                }}>
                  {generatedMetadata.viral_score}/100
                </span>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                  Best Upload Time
                </div>
                <div style={{
                  background: 'rgba(0,200,83,0.1)', padding: '10px 14px',
                  borderRadius: '8px', fontSize: '14px', fontWeight: '500', color: 'var(--primary)'
                }}>
                  {generatedMetadata.best_upload_time}
                </div>
              </div>

              {generatedMetadata.viral_tips && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                    Viral Tips
                  </div>
                  {generatedMetadata.viral_tips.map((tip, i) => (
                    <div key={i} style={{
                      display: 'flex', gap: '8px', marginBottom: '6px',
                      fontSize: '13px', color: 'var(--text-secondary)'
                    }}>
                      <span style={{ color: 'var(--primary)', fontWeight: '700' }}>-</span>
                      {tip}
                    </div>
                  ))}
                </div>
              )}

              {generatedMetadata.recommended_hashtags && (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                    Recommended Hashtags
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {generatedMetadata.recommended_hashtags.map((tag, i) => (
                      <span key={i} style={{
                        background: 'rgba(0,200,83,0.1)', padding: '4px 10px',
                        borderRadius: '20px', fontSize: '12px', color: 'var(--primary)',
                        border: '1px solid rgba(0,200,83,0.2)'
                      }}>
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          {generatedMetadata && (
            <div className="card" style={{ marginBottom: '20px' }}>
              <div className="card-header">
                <span className="card-title">Generated Metadata</span>
                <button
                  className="btn btn-success btn-sm"
                  onClick={applyMetadata}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Apply to Video'}
                </button>
              </div>

              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {[
                  { icon: <FaYoutube style={{ color: '#ff0000' }} />, label: 'YouTube' },
                  { icon: <FaFacebook style={{ color: '#1877f2' }} />, label: 'Facebook' },
                  { icon: <FaTiktok />, label: 'TikTok' },
                  { icon: <FaInstagram style={{ color: '#e1306c' }} />, label: 'Instagram' }
                ].map(p => (
                  <span key={p.label} style={{
                    display: 'flex', alignItems: 'center', gap: '4px',
                    padding: '4px 10px', background: 'var(--bg-dark)',
                    borderRadius: '20px', fontSize: '12px',
                    border: '1px solid var(--border)'
                  }}>
                    {p.icon} {p.label}
                  </span>
                ))}
              </div>

              <MetadataField label="YouTube Title" value={generatedMetadata.youtube_title} field="yt_title" />
              <MetadataField label="YouTube Description" value={generatedMetadata.youtube_description} field="yt_desc" />
              <MetadataField label="Facebook Title" value={generatedMetadata.facebook_title} field="fb_title" />
              <MetadataField label="Facebook Description" value={generatedMetadata.facebook_description} field="fb_desc" />
              <MetadataField label="TikTok Caption" value={generatedMetadata.tiktok_title} field="tt_title" />
              <MetadataField label="Instagram Caption" value={generatedMetadata.instagram_title} field="ig_title" />
              <MetadataField label="Tags" value={generatedMetadata.tags} field="tags" />
              <MetadataField label="Thumbnail AI Prompt" value={generatedMetadata.thumbnail_prompt} field="thumb" />
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <span className="card-title">Chat with Obsidian AI</span>
              {!apiKeySet && <span className="badge badge-warning">API Key Required</span>}
            </div>

            <div style={{
              height: '300px', overflowY: 'auto', marginBottom: '16px',
              padding: '12px', background: 'var(--bg-dark)', borderRadius: '10px',
              display: 'flex', flexDirection: 'column', gap: '12px',
              border: '1px solid var(--border)'
            }}>
              {messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-light)', marginTop: '80px' }}>
                  <MdAutoAwesome style={{ fontSize: '32px', marginBottom: '8px', color: 'var(--primary)', opacity: 0.4 }} />
                  <p style={{ fontSize: '14px' }}>
                    {apiKeySet
                      ? 'Select a video and click Generate Viral Metadata or ask me anything about your content!'
                      : 'Connect your Obsidian AI API key to start chatting'}
                  </p>
                </div>
              ) : (
                messages.map((msg, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                  }}>
                    <div style={{
                      maxWidth: '85%',
                      padding: '10px 14px',
                      borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                      background: msg.role === 'user'
                        ? 'var(--primary-gradient)'
                        : 'var(--bg-card)',
                      color: msg.role === 'user' ? 'white' : 'var(--text-primary)',
                      fontSize: '13px',
                      lineHeight: '1.6',
                      boxShadow: 'var(--shadow-sm)',
                      border: msg.role === 'assistant' ? '1px solid var(--border)' : 'none',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {msg.content}
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                className="form-control"
                placeholder={apiKeySet ? "Ask Obsidian AI to refine titles, suggest hashtags, improve descriptions..." : "Connect API key to chat..."}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                disabled={!apiKeySet || sending}
              />
              <button
                className="btn btn-primary"
                onClick={sendMessage}
                disabled={!apiKeySet || sending || !input.trim()}
                style={{ flexShrink: 0 }}
              >
                {sending
                  ? <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} />
                  : <MdSend />}
              </button>
            </div>
            <p style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '8px' }}>
              Press Enter to send - Ask me to refine any generated metadata
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaudeAI;